import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiExternalLink, FiAward } = FiIcons;

export default function RankingsPage() {
  const { tiles, setCurrentPage } = useAppContext();
  const [sortMode, setSortMode] = useState('pixels');

  const ranked = [...tiles]
    .filter(t => t.title && t.link)
    .sort((a, b) => sortMode === 'pixels' ? (b.w * b.h) - (a.w * a.h) : parseInt(b.id) - parseInt(a.id))
    .slice(0, 30);

  const getRankLabel = (index, arr) => {
    if (index === 0) return { label: '#1', sub: 'TOP SPOT', color: '#FFD700' };
    const prevPx = arr[index - 1].w * arr[index - 1].h;
    const curPx = arr[index].w * arr[index].h;
    if (prevPx === curPx) return { label: 'T-' + (index + 1), sub: 'TIED', color: '#ff00cc' };
    return { label: `#${index + 1}`, sub: '', color: '#ff00cc' };
  };

  const totalInvested = (tile) => ((tile.w * tile.h) * 0.10).toFixed(2);

  return (
    <div style={{ background: '#080012', fontFamily: 'Courier New, monospace', minHeight: '100vh', overflowY: 'auto' }}>
      {/* Sticky header */}
      <div className="sticky top-0 z-10 px-6 pt-8 pb-6 border-b border-pink-500/20"
        style={{ background: 'rgba(8,0,18,0.97)', backdropFilter: 'blur(12px)' }}>
        <button onClick={() => setCurrentPage('home')}
          className="flex items-center gap-2 text-pink-500 hover:text-pink-300 transition-colors text-sm font-bold mb-5 uppercase tracking-widest">
          <FiArrowLeft /> Back to Grid
        </button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FiAward className="text-pink-500 text-sm" />
              <span className="text-pink-500 text-xs font-black uppercase tracking-widest">Live Ownership Board</span>
            </div>
            <h1 className="font-black uppercase leading-none mb-1" style={{ fontFamily: 'Impact,sans-serif', letterSpacing: '2px' }}>
              <span className="text-white" style={{ fontSize: 'clamp(30px,5vw,60px)' }}>PIXEL </span>
              <span style={{ color: '#ff00cc', fontSize: 'clamp(30px,5vw,60px)' }}>RANKINGS</span>
            </h1>
            <p className="text-gray-400 text-sm">Largest permanent footprints on the grid.</p>
          </div>
          <div className="flex rounded-xl overflow-hidden border border-pink-500/30">
            <button onClick={() => setSortMode('pixels')}
              className="px-5 py-2.5 text-xs font-black uppercase tracking-wider transition-all"
              style={sortMode === 'pixels' ? { background: '#ff00cc', color: '#fff' } : { color: '#6b7280' }}>
              Most Pixels
            </button>
            <button onClick={() => setSortMode('recent')}
              className="px-5 py-2.5 text-xs font-black uppercase tracking-wider transition-all"
              style={sortMode === 'recent' ? { background: '#ff00cc', color: '#fff' } : { color: '#6b7280' }}>
              Recently Added
            </button>
          </div>
        </div>
        <div className="mt-4 h-px w-full" style={{ background: 'linear-gradient(90deg,#ff00cc,transparent)' }} />
      </div>

      {/* Rankings list — scrollable */}
      <div className="max-w-4xl mx-auto px-4 py-8 pb-24 space-y-4">
        {ranked.map((tile, idx) => {
          const rank = getRankLabel(idx, ranked);
          const pixels = tile.w * tile.h;
          const isTop = idx === 0;
          return (
            <div key={tile.id}
              className="rounded-xl overflow-hidden transition-all hover:scale-[1.005]"
              style={{
                border: isTop ? '1px solid rgba(255,215,0,0.4)' : '1px solid rgba(255,0,200,0.15)',
                background: isTop ? 'rgba(255,215,0,0.04)' : 'rgba(255,255,255,0.02)',
              }}>
              <div className="flex items-center gap-3 p-4">
                {/* Rank */}
                <div className="w-16 shrink-0 text-center">
                  <div className="font-black leading-none" style={{ color: rank.color, fontFamily: 'Impact,sans-serif', fontSize: 'clamp(20px,3vw,36px)' }}>
                    {rank.label}
                  </div>
                  {rank.sub && <div className="text-[9px] font-black uppercase tracking-widest mt-0.5" style={{ color: rank.color }}>{rank.sub}</div>}
                </div>

                {/* Thumb */}
                <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 border border-gray-700"
                  style={{ background: tile.solidColor || '#1a0030' }}>
                  {tile.image
                    ? <img src={tile.image} alt={tile.title} className="w-full h-full object-cover" />
                    : <div className="w-full h-full" style={{ background: tile.solidColor || '#1a0030' }} />}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="font-black text-white text-base truncate" style={{ fontFamily: 'Impact,sans-serif', letterSpacing: '1px' }}>
                    {tile.title}
                  </div>
                  <a href={tile.link} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs transition-colors hover:text-pink-400 truncate"
                    style={{ color: '#ff00cc' }}>
                    <FiExternalLink className="text-[10px] shrink-0" />
                    <span className="truncate">{tile.link.replace('https://', '')}</span>
                  </a>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1">
                    ${totalInvested(tile)} invested
                  </div>
                </div>

                {/* Pixels + visit */}
                <div className="text-right shrink-0">
                  <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-0.5">Pixels</div>
                  <div className="font-black" style={{ color: rank.color, fontFamily: 'Impact,sans-serif', fontSize: 'clamp(18px,2.5vw,28px)' }}>
                    {pixels.toLocaleString()}
                  </div>
                  <a href={tile.link} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-1 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-wider transition-all hover:scale-105"
                    style={{ border: '1px solid rgba(255,0,200,0.4)', color: '#ff00cc' }}>
                    VISIT <FiExternalLink className="text-[10px]" />
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}